import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowLeft, ExternalLink } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface ClinicianPublicHoldProps {
  eyebrow: string;
  title: string;
  description: string;
}

const setMeta = (name: string, content: string) => {
  let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
  return element;
};

const ClinicianPublicHold = ({
  eyebrow,
  title,
  description,
}: ClinicianPublicHoldProps) => {
  useEffect(() => {
    const previousTitle = document.title;
    const robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const descriptionMeta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const previousRobots = robots?.getAttribute("content") ?? null;
    const previousDescription = descriptionMeta?.getAttribute("content") ?? null;

    document.title = `${title} | Myelina Health`;
    const robotsElement = setMeta("robots", "noindex, follow");
    const descriptionElement = setMeta("description", description);

    return () => {
      document.title = previousTitle;
      if (previousRobots === null) robotsElement.remove();
      else robotsElement.setAttribute("content", previousRobots);
      if (previousDescription === null) descriptionElement.remove();
      else descriptionElement.setAttribute("content", previousDescription);
    };
  }, [description, title]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content" className="pt-32 pb-24 sm:pt-40">
        <section className="container mx-auto max-w-4xl px-6 sm:px-10 lg:px-16">
          <div className="mx-auto max-w-3xl text-center">
            <div className="eyebrow justify-center mx-auto mb-6">{eyebrow}</div>
            <h1 className="font-display text-display-lg text-foreground">{title}</h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-foreground/80">
              {description}
            </p>
          </div>

          <Card className="mx-auto mt-12 max-w-3xl border-primary/20 bg-primary/5 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <ShieldAlert className="mt-1 h-6 w-6 flex-none text-primary" aria-hidden="true" />
              <div className="space-y-3">
                <h2 className="font-display text-2xl text-foreground">
                  Public clinician workflows are paused
                </h2>
                <p className="text-[15px] leading-7 text-foreground/85">
                  This route does not display a patient roster, patient detail, clinical summary, processor inventory, security attestation, submitted inquiry, partnership offer, or operational clinician workspace. It collects and transmits no information.
                </p>
              </div>
            </div>
          </Card>

          <div className="mx-auto mt-8 grid max-w-3xl gap-6 md:grid-cols-2">
            <Card className="border-border/60 bg-card p-6 sm:p-7">
              <h2 className="font-display text-xl text-foreground">Current source boundary</h2>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-foreground/80">
                <li>Retrospective self-reported wellness context only.</li>
                <li>No public clinician or patient-data collection.</li>
                <li>No public roster, summary, export, credential upload, or cohort report.</li>
                <li>No predictive, diagnostic, treatment, monitoring, or emergency representation.</li>
              </ul>
            </Card>

            <Card className="border-border/60 bg-card p-6 sm:p-7">
              <h2 className="font-display text-xl text-foreground">Evidence still required</h2>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-foreground/80">
                <li>Deployed processors, regions, data routes, logs, retention, backups, export, and deletion.</li>
                <li>Authenticated authorization, role, consent, isolation, and audit testing.</li>
                <li>Versioned notices and qualified privacy, legal, clinical, claims, security, and regulatory review.</li>
                <li>Post-deployment verification tied to the exact released commit.</li>
              </ul>
            </Card>
          </div>

          <Card className="mx-auto mt-8 max-w-3xl border-border/60 bg-muted/30 p-6 text-sm leading-6 text-foreground/80">
            <h2 className="font-display text-lg text-foreground">Do not send sensitive information</h2>
            <p className="mt-2">
              Public channels are limited to general business questions. Do not send patient, health, medical, diagnosis, treatment, insurance, credential, license, or other sensitive information.
            </p>
          </Card>

          <div className="mx-auto mt-10 flex max-w-3xl flex-col justify-center gap-3 sm:flex-row">
            <Button asChild variant="outline" size="lg">
              <Link to="/for-clinicians">
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                Return to clinician information
              </Link>
            </Button>
            <Button asChild variant="ghost" size="lg">
              <a href="mailto:hello@myelinahealth.com?subject=General%20clinician%20information">
                General business email
                <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ClinicianPublicHold;
